import { useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader } from 'components/Loader';
import { ProductGlobalForm } from './ProductGlobalForm';

import {
    useGetSuppliersQuery,
    useGetActiveOrganizationsQuery,
    useGetDetailedProductQuery,
    useGetProductPriceRangesQuery,
    useGetProductPricesQuery,
    useGetTagsQuery,
    useUpdatedProductSubscription,
    TotadminPermission,
} from 'data/__generated__';
import { useHasPermission } from 'hooks/useHasPermission';

type ParamTypes = {
    productId: string;
};

export const ProductDetails = () => {
    const { productId = '' } = useParams<ParamTypes>();

    const { data: subscriptionData } = useUpdatedProductSubscription({
        variables: { productId },
    });

    useEffect(() => {
        if (subscriptionData?.updatedProduct) {
            toast.warn("Le produit vient d'être mis à jour");
        }
    }, [subscriptionData]);

    const {
        loading: productLoading,
        data: productData,
        error: productError,
    } = useGetDetailedProductQuery({
        variables: { productId },
    });

    const { loading: tagsLoading, data: tagsData, error: tagsError } = useGetTagsQuery();

    const {
        loading: productRangesLoading,
        data: productRangesData,
        error: productRangesError,
    } = useGetProductPriceRangesQuery();

    // Les prix sont une ressource à part (`priceRanges`) : sans le droit, le bloc prix est verrouillé
    // (`SalesInfoSection`) et la requête ne part pas — sinon « Accès refusé » ferait tomber toute la page.
    const canReadPrices = useHasPermission(TotadminPermission.PriceRangesRead);
    const {
        loading: productPricesLoading,
        data: productPricesData,
        error: productPricesError,
    } = useGetProductPricesQuery({
        variables: { productId },
        skip: !canReadPrices,
    });

    const {
        loading: organizationsLoading,
        data: organizationsData,
        error: organizationsError,
    } = useGetActiveOrganizationsQuery();

    const { loading: suppliersLoading, data: suppliersData, error: suppliersError } = useGetSuppliersQuery();

    if (
        organizationsLoading ||
        productLoading ||
        productPricesLoading ||
        productRangesLoading ||
        tagsLoading ||
        suppliersLoading
    ) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (
        organizationsError ||
        !organizationsData ||
        productError ||
        !productData ||
        productRangesError ||
        !productRangesData ||
        productPricesError ||
        (canReadPrices && !productPricesData) ||
        tagsError ||
        !tagsData ||
        suppliersError ||
        !suppliersData
    ) {
        throw new Error('Une erreur est survenue lors de la récupération du produit');
    }
    const { tags } = tagsData;
    const { detailedProduct } = productData;
    const { activeOrganizations } = organizationsData;
    const { productPriceRanges } = productRangesData;
    const productPrices = productPricesData?.productPrices ?? [];
    const { suppliers } = suppliersData;

    if (!detailedProduct) {
        throw new Error(`Aucun produit avec l'id ${productId} n'a été trouvé`);
    }

    return (
        <Container>
            <ProductGlobalForm
                key={detailedProduct.updatedAt}
                tags={tags}
                organizations={activeOrganizations}
                detailedProduct={detailedProduct}
                productPrices={productPrices}
                productPriceRanges={productPriceRanges}
                suppliers={suppliers}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
